import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks';
import { captureEvent } from '@/lib/analytics';
import { displayNameFromUser } from '@/lib/displayName';
import { ensureHousehold } from '@/services/household.service';
import { updateProfile } from '@/services/user.service';
import { useHouseholdStore } from '@/stores/householdStore';
import {
  useOnboardingStore,
  type EntryChoice,
  type ServerOnboardingProgress,
} from '@/stores/onboardingStore';
import { useUserStore } from '@/stores/userStore';
import { SignOutConfirmModal } from '@/components/domain/onboarding';
import { ForkScreen } from './ForkScreen';
import { JoinWaitState } from './JoinWaitState';
import { OnboardingFlow } from './OnboardingFlow';
import { householdNameFor } from './onboardingCopy';

export interface OnboardingEntryProps {
  user: {
    id: string;
    email?: string | null;
    user_metadata?: Record<string, unknown> | null;
  };
}

/**
 * What the server remembers about this account's onboarding, from the two
 * stores AuthContext fills before `loading` clears (both on initialise and
 * on SIGNED_IN): the `users` row and the resolved membership. Read at mount
 * so a device with no copy of the flow — a fresh browser context, a second
 * phone — resumes where the account was instead of at the fork (YAR-438 QA).
 * "Owns" is deliberately PRIMARY-only: an account whose active membership
 * is someone else's household has not been through the fork.
 */
function serverOnboardingProgress(): ServerOnboardingProgress {
  const profile = useUserStore.getState();
  const hh = useHouseholdStore.getState();
  return {
    step: profile.onboardingStep,
    ownsHousehold:
      hh.resolved && hh.householdId !== null && hh.role === 'PRIMARY',
    profile: {
      displayName: profile.displayName,
      role: profile.profileRole,
      timezone: profile.timezone,
      avatarEmoji: profile.avatarEmoji,
      avatarColor: profile.avatarColor,
    },
  };
}

/**
 * What a signed-in, not-yet-onboarded parent with no pending invite sees
 * (YAR-438). `Onboarding.tsx` has already run every other gate — the invite
 * link, the completed-onboarding redirect, YAR-340's pending-invite check —
 * so this only has to answer one question: did they say what they are here
 * to do?
 *
 *   null     → the fork
 *   'helper' → the wait-state (never creates a household)
 *   'admin'  → the existing OnboardingFlow, from the Founder's letter
 *
 * The admin choice is the one place a household is created now that
 * `users-provision` no longer does it at sign-in. It goes through
 * `ensureHousehold`, whose 409 is the idempotent resume — so "Set up my
 * family instead" from the wait-state and re-picking the option on the fork
 * are the same call and never make a second household.
 *
 * The choice, the step and the profile answers persist in the onboarding
 * store, which is device-local. A device with no copy of them — a fresh
 * browser context, a second phone — is seeded from the server at mount
 * (`serverOnboardingProgress`): a household the account already owns is the
 * admin choice, `users.onboarding_step` is the step, and the `users` row is
 * the profile. That is what makes "close the window, sign in again → resume
 * where you were" hold per ACCOUNT, as the story words it, and not just per
 * device (YAR-438 QA).
 */
export function OnboardingEntry({ user }: OnboardingEntryProps) {
  // Seed BEFORE the first paint, not only in an effect: a `useState`
  // initializer runs synchronously during the initial render, so a
  // different account on the same device never flashes the previous
  // account's wait-state for a frame. StrictMode DOES invoke this
  // initializer twice — what makes that safe is `initFromUser` being
  // idempotent for the same user, not any guarantee about single
  // invocation. The effect below re-seeds on a later user change (an
  // account switch without a full remount).
  useState(() => {
    useOnboardingStore
      .getState()
      .initFromUser(
        user.id,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        serverOnboardingProgress(),
      );
    // A persisted 'admin' is a device-local flag, not proof a household
    // exists on the server, and it can go stale in two ways: the admin
    // reset-user runbook (docs/runbooks/admin-reset-user.md) deletes the
    // owned household and the `public.users` row but keeps `auth.users`, so
    // signing back in on the same device restores 'admin' with nothing
    // behind it; or a sign-out races an in-flight `startAdmin()` (guarded
    // there too) and the flag is written into a store that no longer has an
    // owner. Re-check it here, once, against the household store rather than
    // trusting it: `applyMembership` (AuthContext) marks that store
    // `resolved` — with `householdId: null` when the account owns nothing —
    // BEFORE `loading` clears, on both initialise and SIGNED_IN, so by the
    // time this mounts the signal is authoritative. Deliberately mount-time
    // only, not a subscription: re-checking on every render could loop if a
    // transient boot-resolution failure ever raced the 409 resume path.
    const hh = useHouseholdStore.getState();
    if (
      useOnboardingStore.getState().entryChoice === 'admin' &&
      hh.resolved &&
      hh.householdId === null
    ) {
      useOnboardingStore.getState().setEntryChoice(null);
    }
    return null;
  });
  useEffect(() => {
    useOnboardingStore
      .getState()
      .initFromUser(
        user.id,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        serverOnboardingProgress(),
      );
  }, [user.id]);

  const entryChoice = useOnboardingStore((s) => s.entryChoice);
  const setEntryChoice = useOnboardingStore((s) => s.setEntryChoice);
  const resetStore = useOnboardingStore((s) => s.resetStore);
  const { signOut } = useAuth();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const startAdmin = useCallback(async () => {
    const result = await ensureHousehold(
      householdNameFor(displayNameFromUser(user)),
    );
    // The parent may have signed out while that request was in flight — a
    // sign-out resets the store's userId to null, and a different account
    // signing in on this device before the response lands would give it a
    // new one. Either way the store is no longer this call's to write:
    // committing here would either strand an ownerless 'admin' flag or hand
    // it to the wrong account.
    if (useOnboardingStore.getState().userId !== user.id) return;
    if (result.outcome === 'created') {
      useHouseholdStore
        .getState()
        .setActiveHousehold(result.householdId, 'PRIMARY');
      captureEvent('household_created', { source: 'onboarding_fork' });
    } else {
      // AC-9 (YAR-439): the household already exists, so this is a resume.
      // Back may have walked `currentStep` all the way to the Founder's
      // letter on the way to the fork — continue from the furthest step the
      // parent reached instead, with what they entered still in the store.
      useOnboardingStore.getState().resumeAtFurthestStep();
    }
    // An account that already finished onboarding reaches the fork only when
    // it belongs to no household (left its only family, was removed, or the
    // household was deleted — useHouseholdlessAccount). Choosing to set up a
    // family starts the ADMIN onboarding, which that account has never done,
    // so it is not onboarded any more: clear the stamp on the server. Left in
    // place, the Gmail connect step's OAuth round trip reloads the page, the
    // fresh mount sees "onboarded + has a household" and both gates send the
    // parent to /today, skipping Approved Senders, SMS and Summary (YAR-543).
    // The flow's completion stamps it again.
    //
    // Only the server row changes, not the local store. Within this mount
    // Onboarding.tsx keeps the fork latched; nulling the local flag here
    // would swap it to its pending-invitation loading state and unmount the
    // flow under the parent. Every later mount loads the row, null, and the
    // two gates agree from it: ProtectedRoute sends to `/`, Onboarding shows
    // the admin flow. Runs on 'resumed' too, so a retry after this write
    // failed (the fork shows its error line) still clears it.
    if (useUserStore.getState().onboardingCompletedAt !== null) {
      await updateProfile({ onboarding_completed_at: null });
      if (useOnboardingStore.getState().userId !== user.id) return;
    }
    setEntryChoice('admin');
  }, [user, setEntryChoice]);

  const onContinue = useCallback(
    async (choice: EntryChoice) => {
      if (choice === 'admin') {
        await startAdmin();
        return;
      }
      setEntryChoice('helper');
    },
    [startAdmin, setEntryChoice],
  );

  const confirmSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      // Order matches the spec's transition table (§1.3): signOut(), then
      // resetStore(). It would only matter which comes first if
      // Onboarding.tsx ever stopped swapping to its own "Loading..." screen
      // while `authLoading` is true — today `signOut` holds that flag for
      // the whole request, so this component (the modal included) is
      // unmounted before resetStore() could render anything under it.
      resetStore();
    } catch {
      // AuthContext already recorded the error and set its own `error`
      // state; the parent is still signed in and stays where they were.
    } finally {
      setSigningOut(false);
      setSignOutOpen(false);
    }
  };

  if (entryChoice === 'admin') {
    return <OnboardingFlow onBackToFork={() => setEntryChoice(null)} />;
  }

  return (
    <>
      {entryChoice === 'helper' ? (
        <JoinWaitState
          email={user.email ?? ''}
          onSetUpFamily={startAdmin}
          onSignOut={() => setSignOutOpen(true)}
          onBack={() => setEntryChoice(null)}
        />
      ) : (
        <ForkScreen
          onContinue={onContinue}
          onBack={() => setSignOutOpen(true)}
        />
      )}
      <SignOutConfirmModal
        open={signOutOpen}
        busy={signingOut}
        onConfirm={() => void confirmSignOut()}
        onCancel={() => setSignOutOpen(false)}
      />
    </>
  );
}
